import dayjs from 'dayjs';
import { call, debounce, put } from 'redux-saga/effects';
import {
  createCeaseEnded,
  createCeaseError,
  createCeaseStarted,
  createCeaseSuccess,
  getCeaseEnded,
  getCeaseError,
  getCeaseStarted,
  getCeaseSuccess,
  getCeasesEnded,
  getCeasesError,
  getCeasesStarted,
  getCeasesSuccess,
} from './actions';
import { CeaseActionTypes, ICreateCease, IGetCease, IGetCeases } from './types/actions';
import { createCeaseRequest, getCeaseRequest, getCeasesRequest } from './crud/ceaseCrud';
import { CeaseRequestDetails, CreateCeaseRequestPayload } from './types/cease';
import { getCategoryPairFromRequestType } from './utils/requestType';

const extractErrorMessage = (error: any): string => {
  const jsonApiErrors = error?.response?.data?.errors;
  if (Array.isArray(jsonApiErrors) && jsonApiErrors.length > 0) {
    const titles = jsonApiErrors
      .map((e: any) => String(e?.title ?? '').trim())
      .filter((t: string) => t.length > 0);
    if (titles.length > 0) return titles.join('. ');
  }

  return (
    error?.response?.data?.errors?.[0]?.title ||
    error?.response?.data?.message ||
    error?.message ||
    'Something went wrong'
  );
};

export function* createCease({ payload }: ICreateCease): any {
  yield put(createCeaseStarted());

  try {
    const { customerId, customerName, submittedBy, form } = payload;
    const categoryPair = getCategoryPairFromRequestType(form.requestType);

    const requestedCeaseDate = form.requestedCeaseDate ? dayjs(form.requestedCeaseDate).format('YYYY-MM-DD') : '';

    const requestPayload: CreateCeaseRequestPayload = {
      customer_id: customerId,
      category: categoryPair.category,
      sub_category: categoryPair.subCategory,
      customer_name: customerName,
      submitted_by: submittedBy,
      primary_contact_name: form.primaryContactName,
      primary_contact_email: form.primaryContactEmail,
      primary_contact_phone: form.primaryContactPhone,
      secondary_contact_email: form.secondaryContactEmail || undefined,
      idn: form.idn,
      requested_cease_date: requestedCeaseDate,
      cease_reason: String(form.ceaseReason || ''),
      details: form.details || undefined,
      customer_reference: form.customerReference || undefined,
    };

    const cease: CeaseRequestDetails = yield call(createCeaseRequest, requestPayload);
    yield put(createCeaseSuccess(cease));
  } catch (error: any) {
    yield put(createCeaseError(extractErrorMessage(error)));
  } finally {
    yield put(createCeaseEnded());
  }
}

export function* getCeases({ payload }: IGetCeases): any {
  yield put(getCeasesStarted());

  try {
    const ceases = yield call(getCeasesRequest, payload.customerId);
    yield put(getCeasesSuccess(ceases));
  } catch (error: any) {
    yield put(getCeasesError(extractErrorMessage(error)));
  } finally {
    yield put(getCeasesEnded());
  }
}

export function* getCease({ payload }: IGetCease): any {
  yield put(getCeaseStarted());

  try {
    const cease = yield call(getCeaseRequest, payload.customerId, payload.id);
    yield put(getCeaseSuccess(cease));
  } catch (error: any) {
    yield put(getCeaseError(extractErrorMessage(error)));
  } finally {
    yield put(getCeaseEnded());
  }
}

export default function* ceaseSagas() {
  yield debounce(300, CeaseActionTypes.CREATE_CEASE, createCease);
  yield debounce(300, CeaseActionTypes.GET_CEASES, getCeases);
  yield debounce(300, CeaseActionTypes.GET_CEASE, getCease);
}
